import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

// Local import
import CollectForm from './CollectForm'
import Loader from '../shared/Loader'
import Button from '../ui/Button'
import CollectService from '../../services/api/CollectService'
import CollectImportModal from '../CollectImportModal'
import Toast, { showToast } from '../shared/Toast'

const TOAST_CONTAINER_ID = 'edit-collect-toast'
const IMPORT_SUCCESS_MSG = 'Questionnaire importé avec succès. N\'oubliez pas de sauvegarder.'
const HEADER_BAR = 'tw:sticky tw:z-30 tw:-mt-[15px]! '
  + 'tw:flex! tw:items-center! tw:justify-between! tw:gap-3 tw:px-[15px]! '
  + 'tw:pt-[15px]! tw:pb-[10px]! '
  + 'tw:bg-white! tw:rounded-t-lg! tw:border-b! tw:border-solid! tw:border-gray-200!'
const BACK_LINK = 'tw:flex! tw:items-center! tw:gap-3 tw:text-ds-neutral-highest! tw:no-underline!'
const SECTION_STICKY_OVERLAP = 2

const EditCollect = ({ collectId, hasConditionals = false, hasPlan = false, hasDynamicOptions = false }) => {
  const [data, setData] = useState(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [importedSections, setImportedSections] = useState(null)
  const [conditionsOpen, setConditionsOpen] = useState(false)
  const [conditionsFilterId, setConditionsFilterId] = useState(null)
  const [conditionsQuestionId, setConditionsQuestionId] = useState(null)
  const [stickyTop, setStickyTop] = useState(0)
  const [sectionStickyTop, setSectionStickyTop] = useState(0)
  const headerRef = useRef(null)

  useEffect(() => {
    CollectService.getCollectData(collectId).then(setData)
  }, [collectId])

  useLayoutEffect(() => {
    const computeStickyTop = () => {
      const navbar = document.querySelector('.page-header')
      const isVisible = navbar && window.getComputedStyle(navbar).display !== 'none'
      const navbarHeight = isVisible ? navbar.offsetHeight : 0
      const headerHeight = headerRef.current ? headerRef.current.offsetHeight : 0
      setStickyTop(navbarHeight)
      setSectionStickyTop(navbarHeight + headerHeight - SECTION_STICKY_OVERLAP)
    }
    computeStickyTop()
    window.addEventListener('resize', computeStickyTop)
    return () => window.removeEventListener('resize', computeStickyTop)
  }, [])

  const handleOpenImportModal = useCallback(() => {
    setShowImportModal(true)
  }, [])

  const handleCloseImportModal = useCallback(() => {
    setShowImportModal(false)
  }, [])

  const handleImportSuccess = useCallback(sections => {
    setShowImportModal(false)
    setImportedSections(sections)
    showToast(IMPORT_SUCCESS_MSG, 'success', TOAST_CONTAINER_ID)
  }, [])

  const handleSectionsImported = useCallback(() => {
    setImportedSections(null)
  }, [])

  const handleOpenConditions = useCallback(sectionId => {
    if (!hasConditionals)
      return

    const isId = typeof sectionId === 'number' || typeof sectionId === 'string'
    setConditionsQuestionId(null)
    setConditionsFilterId(isId ? sectionId : null)
    setConditionsOpen(true)
  }, [hasConditionals])

  const handleAddQuestionCondition = useCallback((questionId, sectionId) => {
    if (!hasConditionals)
      return

    setConditionsFilterId(sectionId ?? null)
    setConditionsQuestionId(questionId)
    setConditionsOpen(true)
  }, [hasConditionals])
  const handleCloseConditions = useCallback(() => setConditionsOpen(false), [])

  return (
    <div className="tw:relative" style={{ '--section-sticky-top': `${sectionStickyTop}px` }}>
      <Toast containerId={TOAST_CONTAINER_ID} />
      <div className="tw:-mx-[15px]! tw:bg-white! tw:rounded-lg!">
        <div ref={headerRef} className={HEADER_BAR} style={{ top: stickyTop }}>
          <a href="/collects" className={BACK_LINK}>
            <i className="fas fa-arrow-left tw:text-base" />
            <span className="tw:text-base! tw:font-medium! tw:leading-normal!">Édition de la collecte</span>
          </a>
          {hasConditionals ? (
            <Button variant="neutral" icon="cog" onClick={handleOpenConditions}>
              Configurer les conditions
            </Button>
          ) : null}
        </div>
        <div className="tw:p-6">
          {data ? (
            <CollectForm
              data={data}
              hasConditionals={hasConditionals}
              hasPlan={hasPlan}
              hasDynamicOptions={hasDynamicOptions}
              importedSections={importedSections}
              conditionsOpen={conditionsOpen}
              conditionsFilterId={conditionsFilterId}
              conditionsQuestionId={conditionsQuestionId}
              onOpenImportModal={handleOpenImportModal}
              onSectionsImported={handleSectionsImported}
              onOpenConditions={handleOpenConditions}
              onAddQuestionCondition={handleAddQuestionCondition}
              onCloseConditions={handleCloseConditions}
            />
          ) : (
            <Loader
              size="small"
              text="Chargement de la collect en cours..."
            />
          )}
        </div>
      </div>
      <CollectImportModal
        collectId={collectId}
        isOpen={showImportModal}
        onClose={handleCloseImportModal}
        onSuccess={handleImportSuccess}
      />
    </div>
  )
}

export default EditCollect
